package com.arena_web.ui.components.overlay

import androidx.compose.runtime.Composable
import com.arena_web.data.dto.PersonDto
import com.arena_web.data.dto.ProfileCardDto
import com.arena_web.data.dto.UserDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.events.PopupOverlayFadeIn
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.toAnimation
import org.jetbrains.compose.web.css.*

// =============================================================================
// Mobile "It's MATCH" popup — Figma 8407:2791.
//
// The phone counterpart to the desktop [MatchPopup]: a full-screen var(--l-s2, #09142D)
// surface with diagonal-shard corner art, two overlapping player cards, the
// live "It's / MATCH" wordmark + subtitle, and two stacked full-width CTAs.
// Same trigger (PeopleViewModel.matchedPerson) and same data as the desktop
// popup; People.kt picks this variant on phone viewports.
// =============================================================================

private const val SURFACE_BG = "var(--l-popup, var(--l-s2, #09142D))"
// Point-symmetric shard cluster, reused at both corners (top-left rotated 180°).
private const val SHARD_URL = "/images/match-shard-bottom.svg"

// Cards are PlayerCardSize.Medium (130×202); the matched card overlaps the
// caller's by ~36px and sits on top (Figma 8407:2960).
private const val CARD_WIDTH = 130
private const val CARD_HEIGHT = 202
private const val CARD_OVERLAP = 36
private const val CARDS_ROW_WIDTH = CARD_WIDTH * 2 - CARD_OVERLAP

@Composable
fun MobileMatchPopup(
    matched: PersonDto?,
    currentUser: UserDto?,
    currentUserCard: ProfileCardDto?,
    sportName: String,
    onClose: () -> Unit,
    onGoToProfile: (userId: String) -> Unit,
) {
    if (matched == null) return
    val sportColors = SportColors.forSport(sportName.lowercase())
    val t = LocalAppStrings.current

    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(1100)
            .styleModifier {
                property("background", SURFACE_BG)
                property("overflow", "hidden")
            }
            .animation(
                PopupOverlayFadeIn.toAnimation(
                    duration = Constants.ANIMATION_FAST.ms,
                    timingFunction = AnimationTimingFunction.EaseOut,
                )
            ),
        contentAlignment = Alignment.Center,
    ) {
        // ── Corner shard art (non-interactive). ──
        Box(modifier = Modifier.styleModifier {
            property("position", "absolute"); property("top", "0"); property("left", "0")
            property("width", s(240)); property("height", s(240))
            property("background", "url('$SHARD_URL') center / 100% 100% no-repeat")
            property("transform", "rotate(180deg)")
            property("pointer-events", "none")
        })
        Box(modifier = Modifier.styleModifier {
            property("position", "absolute"); property("bottom", "0"); property("right", "0")
            property("width", s(260)); property("height", s(260))
            property("background", "url('$SHARD_URL') center / 100% 100% no-repeat")
            property("pointer-events", "none")
        })
        // ── Faint halftone-dot fields flanking the wordmark. ──
        DotField(Modifier.styleModifier {
            property("position", "absolute"); property("left", "0"); property("top", "54%")
            property("-webkit-mask", "linear-gradient(90deg, transparent, #000 80%)")
            property("mask", "linear-gradient(90deg, transparent, #000 80%)")
        })
        DotField(Modifier.styleModifier {
            property("position", "absolute"); property("right", "0"); property("top", "54%")
            property("-webkit-mask", "linear-gradient(270deg, transparent, #000 80%)")
            property("mask", "linear-gradient(270deg, transparent, #000 80%)")
        })

        // ── Foreground content column. ──
        Column(
            modifier = Modifier
                .fillMaxSize()
                .styleModifier {
                    property("position", "relative")
                    property("z-index", "1")
                    property("padding", "0 ${s(20)}")
                    property("box-sizing", "border-box")
                },
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Box(modifier = Modifier.flexGrow(1))

            MatchCardsRow(
                matched = matched,
                currentUser = currentUser,
                currentUserCard = currentUserCard,
                sportName = sportName,
            )

            Box(modifier = Modifier.sHeight(24))

            // ── "It's" / "MATCH" / subtitle. ──
            Column(
                modifier = Modifier.fillMaxWidth().sGap(6),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                SpanText(
                    t.matchItsWord,
                    modifier = Modifier
                        .sFontSize(12)
                        .fontWeight(AppTypography.Regular)
                        .color(com.arena_web.ui.theme.ThemeVars.Text1)
                        .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
                )
                SpanText(
                    t.matchWord.uppercase(),
                    modifier = Modifier
                        .sFontSize(26)
                        .fontWeight(AppTypography.Bold)
                        .sLineHeight(26)
                        .sLetterSpacing(0.52)
                        .color(sportColors.primary)
                        .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
                        .styleModifier { property("text-align", "center") }
                )
                SpanText(
                    t.matchSubtitle,
                    modifier = Modifier
                        .sFontSize(16)
                        .fontWeight(AppTypography.Regular)
                        .sLineHeight(24)
                        .sLetterSpacing(0.16)
                        .color(com.arena_web.ui.theme.ThemeVars.Text1)
                        .styleModifier { property("text-align", "center") }
                )
            }

            Box(modifier = Modifier.flexGrow(1))

            // ── Stacked full-width CTAs. ──
            Column(
                modifier = Modifier.fillMaxWidth().sGap(12),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                CtaButton(
                    label = t.matchGoToProfile,
                    primary = true,
                    sportColors = sportColors,
                    onClick = { onGoToProfile(matched.userId) },
                )
                CtaButton(
                    label = t.matchKeepSwiping,
                    primary = false,
                    sportColors = sportColors,
                    onClick = onClose,
                )
            }

            Box(modifier = Modifier.styleModifier {
                property("height", "calc(${s(24)} + env(safe-area-inset-bottom))")
            })
        }
    }
}

@Composable
private fun DotField(modifier: Modifier) {
    Box(modifier = modifier.styleModifier {
        property("width", s(96))
        property("height", s(72))
        property("pointer-events", "none")
        property("background-image", "radial-gradient(circle, rgba(60, 121, 241, 0.45) 1px, transparent 1.6px)")
        property("background-size", "${s(7)} ${s(7)}")
    })
}

@Composable
private fun MatchCardsRow(
    matched: PersonDto,
    currentUser: UserDto?,
    currentUserCard: ProfileCardDto?,
    sportName: String,
) {
    Box(
        modifier = Modifier.styleModifier {
            property("position", "relative")
            property("width", s(CARDS_ROW_WIDTH))
            property("height", s(CARD_HEIGHT))
            property("flex-shrink", "0")
        }
    ) {
        // Left — caller's main card.
        Box(modifier = Modifier.styleModifier {
            property("position", "absolute"); property("left", "0"); property("top", "0")
            property("width", s(CARD_WIDTH)); property("height", s(CARD_HEIGHT))
            property("z-index", "1")
        }) {
            PlayerCard(
                playerName = displayName(
                    fullName = currentUser?.fullName,
                    fallback = currentUser?.username ?: LocalAppStrings.current.matchYouFallback,
                    dateOfBirthIso = currentUser?.dateOfBirth,
                ),
                rating = 0,
                position = "",
                sport = sportName,
                size = PlayerCardSize.Medium,
                avatarUrl = currentUserCard?.avatarUrl,
                hideRating = true,
                hideShadow = true,
                cardAssetsBasePath = currentUserCard?.assetsBasePath,
                cardTextColor = currentUserCard?.textColor,
            )
        }
        // Right — matched person, on top.
        Box(modifier = Modifier.styleModifier {
            property("position", "absolute"); property("right", "0"); property("top", "0")
            property("width", s(CARD_WIDTH)); property("height", s(CARD_HEIGHT))
            property("z-index", "2")
        }) {
            PlayerCard(
                playerName = displayNameWithAge(
                    fullName = matched.fullName,
                    fallback = matched.username,
                    age = matched.age,
                ),
                rating = 0,
                position = "",
                sport = matched.cardTemplateName ?: sportName,
                size = PlayerCardSize.Medium,
                avatarUrl = matched.avatarUrl,
                hideRating = true,
                hideShadow = true,
                cardAssetsBasePath = matched.cardAssetsBasePath,
                cardTextColor = matched.cardTextColor,
            )
        }
    }
}

@Composable
private fun CtaButton(
    label: String,
    primary: Boolean,
    sportColors: SportColorScheme,
    onClick: () -> Unit,
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .sHeight(54)
            .sBorderRadius(100)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("box-sizing", "border-box")
                if (primary) {
                    val p = sportColors.primary.toRgb()
                    property("background", "rgb(${p.red}, ${p.green}, ${p.blue})")
                } else {
                    property("border", "0.3px solid var(--arena-border-top)")
                    property(
                        "background",
                        "linear-gradient(4deg, rgba(0, 59, 174, 0.08) 27%, rgba(3, 87, 230, 0.08) 68%)"
                    )
                }
            },
        contentAlignment = Alignment.Center,
    ) {
        SpanText(
            label,
            modifier = Modifier
                .sFontSize(16)
                .fontWeight(AppTypography.Medium)
                .sLineHeight(18)
                .sLetterSpacing(0.032)
                .color(if (primary) org.jetbrains.compose.web.css.Color(sportColors.onPrimary.toString()) else com.arena_web.ui.theme.ThemeVars.Text2)
        )
    }
}

// "JAMES, 26" — uppercase first name + age from ISO date-of-birth.
private fun displayName(fullName: String?, fallback: String, dateOfBirthIso: String?): String {
    val first = firstNameOf(fullName).ifBlank { fallback }
    val age = ageFromIso(dateOfBirthIso)
    return if (age != null) "${first.uppercase()}, $age" else first.uppercase()
}

private fun displayNameWithAge(fullName: String?, fallback: String, age: Int): String {
    val first = firstNameOf(fullName).ifBlank { fallback }
    return "${first.uppercase()}, $age"
}

private fun firstNameOf(fullName: String?): String =
    fullName.orEmpty().trim().substringBefore(' ').trim()

private fun ageFromIso(iso: String?): Int? {
    if (iso.isNullOrBlank()) return null
    val datePart = iso.substringBefore('T').trim()
    val parts = datePart.split('-')
    if (parts.size != 3) return null
    val (y, m, d) = parts.map { it.toIntOrNull() ?: return null }
    val today = kotlin.js.Date()
    val nowYear = today.getFullYear()
    val nowMonth = today.getMonth() + 1
    val nowDay = today.getDate()
    var years = nowYear - y
    val hadBirthday = nowMonth > m || (nowMonth == m && nowDay >= d)
    if (!hadBirthday) years -= 1
    return years.coerceAtLeast(0)
}

